<template>
  <div class="hello">
    <h1>Tracking Police Brutality at the 2020 Protests</h1>
    <div v-if="stateChosen()" class="outer">
      <div class="chartContainer" align="center">
        <brutality-chart
          :chartData="allIncidents"
          :options="options"
        />
      </div>
      <input type="radio" id="one" value="category" v-model="picked" selected>
      <label for="one">Attacks</label>
      <input type="radio" id="two" value="victim" v-model="picked">
      <label for="two">Victims</label>
    </div>
    <div v-else>
      <image-slider></image-slider>
    </div>
    <h4>Select a State</h4>
    <div class="outer">
      <div class="stateSelect">
        <v-select
          class="style-chooser"
          v-model="selected"
          :options="['All States','Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','Washington DC','West Virginia','Wisconsin','Wyoming', 'Unknown Location']"
        />
      </div>
      <div v-if="stateChosen()">
        <div class="stateSelect">
          <v-select 
            class="style-chooser left-half"
            v-model="filterViolence"
            placeholder="Filter Attacks..."
            :reduce="v => v.value"
            label="label"
            :options="[{label: 'Shot at', value: 0}, {label:'Tear Gassed', value: 1}, {label:'Hit by Vehicle', value: 2}, {label:'Hit by Explosive', value:3}, {label: 'Tazed', value: 4}, {label:'Physically Assaulted', value: 5},{label:'Property Destroyed', value: 6}, {label:'Other', value: 7}]"
          />
          <v-select 
            class="style-chooser right-half"
            v-model="filterVictims"
            placeholder="Filter Victims..."
            :options="['Press', 'Protestor', 'Child', 'Medic', 'Bystander']"
          />
        </div>
        <state :state="selected" :stateData="dataForState"></state>
      </div>
      <div v-else>
        <br>
      </div>
    </div>
    <div class="bottomContainer">
      <h5>
        So far there are <b>{{incidents}}</b> reported incidents of police brutality at the 2020 protests.
      </h5> 
      <br> We cannot let this violence be ignored!
      Please keep submitting instances of police violence on the <a href="https://www.reddit.com/r/2020PoliceBrutality/">r/2020PoliceBrutality</a> subreddit. All data is from their <a href="https://github.com/2020PB/police-brutality">GitHub repository</a>. Leave feedback <a href="https://www.reddit.com/r/2020PoliceBrutality/comments/h00mz4/additional_tags_for_the_2020_police_brutality_data/">here</a>. Thanks to Siegrid T, Daniel S and Audrey S for data tagging.<br>
      Black Lives Matter! Keep fighting for justice!
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import vSelect from "vue-select";
import State from "./State"
import BrutalityChart from "./BrutalityChart"
import ImageSlider from "./ImageSlider"

Vue.component("v-select", vSelect);
Vue.component("state", State);
Vue.component("brutality-chart", BrutalityChart);
Vue.component("image-slider", ImageSlider);

import "vue-select/dist/vue-select.css";
import json from '../assets/all-data-tagged.json';

const SHOOT = ["beanbag", "rubber bullets", "pepper bullets", "deterrent rounds", "projectiles", "real bullets", "tear gas canister"]
const GAS = ["tear gas", "mace", "tear gas canister", "pepper spray"]
const ASSAULT = ["shove", "beat", "tackle", "knee", "push", "choke", "batons", "shields", "throw"]

const PROPERTY = ["property destruction"]
const CAR = ["SUV", "policecar", "horse", "bike"]
const TAZE = ["taser", "stun gun"]
const EXPLOSIVE = ["explosive", "flashbangs", "paint cannister"]
const OTHER = ["hide badges", "threaten", "inhumane treatment", "arrest"]

const all_violence = [SHOOT, GAS, CAR, EXPLOSIVE, TAZE, ASSAULT, PROPERTY, OTHER]

export default {
  name: 'Home',

  data: function () {
    return {
      selected: "",
      picked: "category",
      filterVictims: '',
      filterViolence: '',
      myJson: json,
      incidents: json.data.length,
      options: {
        legend: {
          display: false,
          // fontColor: 'white'
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          yAxes: [{
              display: true,
              scaleLabel: {
                display: true,
                labelString: "Number of Cases",
                fontColor: 'white',
              },
              ticks: {
                fontColor: 'white',
                beginAtZero: true,
                callback: function(value) {if (value % 1 === 0) {return value;}}
              },
              gridLines: {
                display: true,
                zeroLineColor: 'white',
                color: '#d3d3d3'
              }
          }],
          xAxes: [{
            display: true,
            ticks: {
              fontColor: 'white'
            },
            gridLines: {
              display: true,
              zeroLineColor: 'white',
              color: '#d3d3d3'
            }
          }]
        }
      }
    }
  },
  computed: {
    dataForState: function() {
      if (this.selected === 'All States') {
        return this.filterByVictim(this.filterByViolence(json.data, this.filterViolence), this.filterVictims)
      } else {
        return this.filterByVictim(this.filterByViolence(json.data.filter(i => i.state === this.selected), this.filterViolence), this.filterVictims)
      }
    },
    dataCount: function() {
      return json.data.size
    },
    allIncidents: function() {
      var fullList = [];
      if (this.selected === 'All States') {
        fullList = this.filterByVictim(this.filterByViolence(json.data, this.filterViolence), this.filterVictims)
      } else {
        fullList = this.filterByVictim(this.filterByViolence(json.data.filter(i => i.state === this.selected), this.filterViolence), this.filterVictims)
      }

      if (this.picked === "category") {
        const dataList = fullList.reduce(function(accumulator, item) {
          const is_car = item.tags.filter(value => CAR.includes(value)).length;
          const is_property = item.tags.filter(value => PROPERTY.includes(value)).length;
          const is_taze = item.tags.filter(value => TAZE.includes(value)).length;
          const is_explode = item.tags.filter(value => EXPLOSIVE.includes(value)).length;
          const is_shoot = item.tags.filter(value => SHOOT.includes(value)).length;
          const is_gas = item.tags.filter(value => GAS.includes(value)).length;
          const is_assault = item.tags.filter(value => ASSAULT.includes(value)).length
          const is_other = item.tags.filter(value => OTHER.includes(value)).length
          if (is_shoot) {
            accumulator[0] += 1
          }
          if (is_gas) {
            accumulator[1] += 1
          }
          if (is_assault) {
            accumulator[2] += 1
          }
          if (is_car || is_taze || is_explode || is_property || is_other) {
            accumulator[3] += 1
          }
          return accumulator
        }, [0, 0, 0, 0])

        return {
          labels: ["Shot At", "Tear Gassed", "Physically Assaulted", "Other"],
          datasets: [
            {
              label: 'Number of Cases ',
              backgroundColor: '#c40000',
              data: dataList
            }
          ],
        }
      } else {
        const dataList = fullList.reduce(function(accumulator, item) {
          if (item.against === "Protestor") {
            accumulator[0] += 1
          }
          if (item.against === "Medic") {
            accumulator[1] += 1
          }
          if (item.against === "Child") {
            accumulator[2] += 1
          }
          if (item.against === "Press") {
            accumulator[3] += 1
          }
          if (item.against === "Bystander") {
            accumulator[4] += 1
          }
          return accumulator
        }, [0, 0, 0, 0, 0])

        return {
          labels: ["Protestors", "Medics", "Child", "Press", "Bystander"],
          datasets: [
            {
              label: 'Number of Cases',
              backgroundColor: '#0000FF',
              data: dataList
            }
          ],
        }
      }
    }
  },
  methods: {
    stateChosen: function() {
      return !(this.selected === "" || this.selected === null)
    },
    filterByVictim: function(data, victim) {
      if (!(victim === '') && !(victim == undefined)) {
        return data.filter(i => i.against === victim)
      } else {
        return data
      }
    },
    filterByViolence: function(data, v) {
      if (!(v === '') && !(v == undefined)) {
        return data.filter(i => (i.tags.filter(value => all_violence[v].includes(value)).length > 0))
      } else {
        return data
      }
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.chartContainer {
  display: table;
  margin: 0 auto;
  width: 75%;
}
.bottomContainer {
  display: table;
  margin: 0 auto;
  width: 75%;
  padding-bottom: 30px;
}

.v-select.style-chooser.vs--single.vs--searchable {
  background: #fff;
  border: none;
  border-radius: 4px;
  color: #000;
}

.left-half {
  float: left;
  width: 48%;
}

.right-half {
  float: right;
  width: 48%;
}

.stateSelect {
  display: table;
  margin: 0 auto;
  width: 50%;
  margin-bottom: 15px;
}
.outer {
  width: 100%;
}
h4 {
  margin: 40px 0 0;
}
ul {
  list-style-type: none;
  padding: 0;
}
li {
  display: inline-block;
  margin: 0 10px;
}
a {
  color: #42b983;
}
#two {
  margin-left: 15px;
}
label {
  margin-right: 15px;
  margin-left: 2px;
}


</style>
